import React, { useMemo } from 'react';
import { Typography, Grid2 } from '@mui/material';
import { withStyles } from '@mui/styles';
import { Controller, useFormContext } from 'react-hook-form';
import { COLORS } from '../../../utils/Application_Constants';
import ExpenseTextField from './ExpenseTextField';
import ExpenseServiceDate from './ExpenseServiceDate';
import ExpenseDateRange from './ExpenseDateRange';
import ExpenseCategoryType from './ExpenseCategoryType';
import ExpenseBillType from './ExpenseBillType';
import ExpenseInvoiceNumber from './ExpenseInvoiceNumber';
import { valueAsMileage } from '../../Utils/ValueAsUtils';
import MiscExpenseDescription from './MiscExpenseDescription';
import ExpenseDescription from './ExpenseDescription';
import { ExpenseStatusValuesEnum } from './ExpenseStatus.enum';
import { ExpenseModeEnum } from './ExpenseMode.enum';

const styles = (theme) => ({
    formHeader: {
        borderBottom: `1px solid ${COLORS.LT_MIDNIGHT25}`,
        paddingBottom: 8,
        marginBottom: 16,
    },
});

const VaultDetailsForm = (props) => {
    const { classes, index, expense, expenseCategories, originalExpenseEntries, isViewOnly, mode } =
        props;
    const { control, watch, errors } = useFormContext();
    const expenseCategory = watch(`expenseEntries[${index}].category`);
    const expenseType = watch(`expenseEntries[${index}].type`);
    const isExpenseTravel = expenseCategory && expenseCategory === 'Travel';
    const isMiscExpense = expenseCategory === 'Misc' && expenseType === 'Misc';
    const canRemoveTravelDateFields =
        expenseType === 'Baggage' || expenseType === 'Fuel' || expenseType === '';
    const isVault = true;
    const hasMileage =
        expenseType && expenseCategory && expenseCategory === 'Travel' && expenseType === 'Mileage';
    // Get the original status from the originalExpenseEntries
    const originalStatus = useMemo(() => {
        if (expense.id && originalExpenseEntries?.length > 0) {
            const originalExpense = originalExpenseEntries.find((entry) => entry.id === expense.id);
            return originalExpense ? originalExpense.status : expense.status;
        }
        return expense.status; // Fallback to current status if no original entry is found
    }, [expense.id, originalExpenseEntries, expense.status]);
    // Track the current status being edited
    const currentStatus = watch(`expenseEntries[${index}].status`);
    // Compute isPosted based on original status and current transient state
    const isPosted = useMemo(
        () =>
            originalStatus === 'Posted' ||
            (currentStatus === 'OnHoldReimbursement' && originalStatus === 'Posted'),
        [originalStatus, currentStatus]
    );
    const isPendingSubmission = expense.status === 'PendingSubmission';
    const expenseTypes = useMemo(() => {
        if (!expenseCategory) return [];
        if (!expenseCategories) return [];
        const categoryExpenseTypes = expenseCategories.find(
            (option) => option.category === expenseCategory
        );
        return categoryExpenseTypes ? categoryExpenseTypes.types : [];
    }, [expenseCategory, expenseCategories]);
    const originalExpense = (function () {
        // If my tab original db expense is posted disable
        if (
            expense.id &&
            expense.id > 0 &&
            originalExpenseEntries &&
            originalExpenseEntries.length > 0
        ) {
            return originalExpenseEntries.find((entry) => entry.id === expense.id);
        }
        return null;
    })();
    const isAnyExpensePosted = (function () {
        // Check if anything is Posted
        if (originalExpenseEntries && originalExpenseEntries.length > 0) {
            return originalExpenseEntries.some((entry) => entry.status === 'Posted');
        }
        return false;
    })();

    return (
        <>
            <Typography variant="h5" component="h5" className={classes.formHeader}>
                Expense Details
            </Typography>
            <Grid2 container spacing={3}>
                <Grid2 size={{ xs: 12 }}>
                    <ExpenseBillType
                        index={index}
                        expense={expense}
                        isVault={isVault}
                        isSGA={[]}
                        isPosted={isPosted}
                        isPendingSubmission={isPendingSubmission}
                        isViewOnly={isViewOnly}
                        mode={mode}
                    />
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                    <ExpenseCategoryType
                        expenseTypes={expenseTypes}
                        index={index}
                        expense={expense}
                        expenseCategories={expenseCategories}
                        isSGA={[]}
                        isPosted={isPosted}
                        isPendingSubmission={isPendingSubmission}
                        isViewOnly={isViewOnly}
                        mode={mode}
                    />
                </Grid2>
                {isMiscExpense && (
                    <MiscExpenseDescription
                        index={index}
                        expense={expense}
                        isMiscExpense={isMiscExpense}
                    />
                )}
                {isExpenseTravel && !canRemoveTravelDateFields && (
                    <ExpenseDateRange
                        index={index}
                        expense={expense}
                        isPosted={isPosted}
                        isPendingSubmission={isPendingSubmission}
                        isViewOnly={isViewOnly}
                    />
                )}
                <Grid2 container spacing={3} size={12}>
                    <ExpenseServiceDate
                        index={index}
                        expense={expense}
                        mode={ExpenseModeEnum.Vault}
                        isViewOnly={isViewOnly}
                    />

                    <ExpenseInvoiceNumber
                        index={index}
                        expense={expense}
                        isReimbursement
                        isPendingSubmission={isPendingSubmission}
                        isPosted={isPosted}
                        isViewOnly={isViewOnly}
                        mode={ExpenseModeEnum.Vault}
                    />
                </Grid2>
                <ExpenseDescription index={index} expense={expense} isViewOnly={isViewOnly} />

                {hasMileage && (
                    <Grid2 item size={{ xs: 12, md: 6 }}>
                        <Controller
                            control={control}
                            name={`expenseEntries[${index}].mileage`}
                            defaultValue={expense.mileage}
                            rules={{
                                required:
                                    expense?.status !== ExpenseStatusValuesEnum.NonReimbursable &&
                                    expense?.status !== ExpenseStatusValuesEnum.Abandoned
                                        ? 'Miles driven is required'
                                        : false,
                            }}
                            render={({ onChange, value }) => (
                                <ExpenseTextField
                                    id="miles-driven"
                                    onChange={(e) => onChange(valueAsMileage(e.target.value))}
                                    value={value}
                                    placeholder="Enter number of miles"
                                    label="Miles driven"
                                    required={
                                        expense?.status !==
                                            ExpenseStatusValuesEnum.NonReimbursable &&
                                        expense?.status !== ExpenseStatusValuesEnum.Abandoned
                                    }
                                    disabled={isPosted || isPendingSubmission || isViewOnly}
                                    error={Boolean(errors.expenseEntries?.[index]?.mileage)}
                                    helperText={errors.expenseEntries?.[index]?.mileage?.message}
                                />
                            )}
                        />
                    </Grid2>
                )}
                {/* <ExpenseComments index={index} expense={expense} isPosted={isPosted} isPendingSubmission={isPendingSubmission} isViewOnly={isViewOnly}/> */}
            </Grid2>
        </>
    );
};

export default withStyles(styles)(VaultDetailsForm);
